import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
/**
 * Public-facing survey response page. Reached via /s/<token> in the
 * shareable link the HR team emails. No auth required — anyone holding
 * the link can submit one response.
 */
export function PublicSurveyPage() {
    const { token } = useParams();
    const [answers, setAnswers] = useState({});
    const [npsValue, setNpsValue] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const survey = useQuery({
        queryKey: ['public-survey', token],
        queryFn: async () => {
            const r = await fetch(`/api/v1/hrms/surveys/public/${token}/`);
            if (!r.ok) {
                if (r.status === 410) {
                    const d = await r.json();
                    throw new Error(d?.detail || 'This survey is closed.');
                }
                throw new Error('Survey not found or no longer active.');
            }
            return r.json();
        },
        retry: false,
    });
    const submit = useMutation({
        mutationFn: async () => {
            const r = await fetch(`/api/v1/hrms/surveys/public/${token}/submit/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    answers,
                    nps_score: npsValue,
                }),
            });
            if (!r.ok) {
                const body = await r.json().catch(() => ({}));
                const detail = body?.error?.message
                    || body?.error?.details?.answers
                    || body?.detail
                    || `HTTP ${r.status}`;
                throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
            }
            return r.json();
        },
        onSuccess: () => setSubmitted(true),
        onError: (err) => setError(err.message || 'Submission failed.'),
    });
    function setAns(qid, value) {
        setAnswers((a) => ({ ...a, [qid]: value }));
    }
    if (survey.isPending) {
        return _jsx("div", { className: "mx-auto max-w-2xl p-8 text-sm text-muted-foreground", children: "Loading\u2026" });
    }
    if (survey.error) {
        return (_jsx("div", { className: "mx-auto max-w-lg p-8", children: _jsxs("div", { className: "rounded-lg border border-amber-300 bg-amber-50 p-6 text-amber-800", children: [_jsx("h1", { className: "text-lg font-semibold", children: "Survey unavailable" }), _jsx("p", { className: "mt-2 text-sm", children: survey.error.message })] }) }));
    }
    if (submitted) {
        return (_jsx("div", { className: "mx-auto max-w-lg p-8", children: _jsxs("div", { className: "rounded-lg border border-emerald-300 bg-emerald-50 p-6 text-emerald-800", children: [_jsx("h1", { className: "text-lg font-semibold", children: "Thanks for your response!" }), _jsxs("p", { className: "mt-2 text-sm", children: ["We've recorded your answers. ", survey.data?.is_anonymous && 'Submission is anonymous — your identity is not stored with the response.'] })] }) }));
    }
    const s = survey.data;
    // Find a question marked as NPS-typed so the value box shows up if used.
    const hasNps = (s.questions ?? []).some((q) => q.type === 'nps');
    return (_jsx("div", { className: "min-h-screen bg-muted/30 py-8", children: _jsxs("form", { className: "mx-auto max-w-2xl space-y-6 rounded-lg border bg-card p-6 shadow-sm", onSubmit: (e) => { e.preventDefault(); setError(null); submit.mutate(); }, children: [_jsxs("header", { children: [_jsx("h1", { className: "text-2xl font-semibold", children: s.name }), s.description && (_jsx("p", { className: "mt-2 whitespace-pre-wrap text-sm text-muted-foreground", children: s.description })), s.is_anonymous && (_jsx("p", { className: "mt-3 inline-block rounded bg-blue-50 px-2 py-1 text-xs text-blue-700", children: "Your response is anonymous \u2014 your identity is not stored." }))] }), _jsx("ol", { className: "space-y-5", children: (s.questions ?? []).map((q, idx) => (_jsxs("li", { className: "space-y-2", children: [_jsxs("label", { className: "block text-sm font-medium", children: [idx + 1, ". ", q.prompt, q.required && _jsx("span", { className: "ms-1 text-red-600", children: "*" })] }), q.help_text && (_jsx("p", { className: "text-xs text-muted-foreground", children: q.help_text })), _jsx(QuestionInput, { q: q, value: answers[q.id], onChange: (v) => setAns(q.id, v), onNps: (v) => setNpsValue(v) })] }, q.id))) }), error && (_jsx("p", { className: "rounded-md bg-red-50 px-3 py-2 text-sm text-red-700", children: error })), _jsx("div", { className: "flex items-center justify-end border-t pt-4", children: _jsx("button", { type: "submit", disabled: submit.isPending, className: "rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50", children: submit.isPending ? 'Submitting…' : 'Submit response' }) }), _jsx("p", { className: "text-center text-[10px] text-muted-foreground", children: "Powered by Vulcan ERP \u00B7 this link is shareable; one response per person is the polite expectation but not enforced." })] }) }));
}
function QuestionInput({ q, value, onChange, onNps, }) {
    switch (q.type) {
        case 'short_text':
        case 'email':
            return (_jsx("input", { type: q.type === 'email' ? 'email' : 'text', value: value ?? '', onChange: (e) => onChange(e.target.value), required: q.required, className: "block w-full rounded-md border border-input bg-background px-3 py-2 text-sm" }));
        case 'long_text':
            return (_jsx("textarea", { value: value ?? '', onChange: (e) => onChange(e.target.value), required: q.required, rows: 4, className: "block w-full rounded-md border border-input bg-background px-3 py-2 text-sm" }));
        case 'date':
            return (_jsx("input", { type: "date", value: value ?? '', onChange: (e) => onChange(e.target.value), required: q.required, className: "block w-full rounded-md border border-input bg-background px-3 py-2 text-sm" }));
        case 'yes_no':
            return (_jsx("div", { className: "flex gap-3", children: ['Yes', 'No'].map((opt) => (_jsxs("label", { className: "inline-flex items-center gap-2 text-sm", children: [_jsx("input", { type: "radio", name: `q-${q.id}`, value: opt, checked: value === opt, required: q.required, onChange: () => onChange(opt) }), opt] }, opt))) }));
        case 'single_choice':
            return (_jsx("div", { className: "space-y-2", children: (q.options ?? []).map((opt) => (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "radio", name: `q-${q.id}`, value: opt, checked: value === opt, required: q.required, onChange: () => onChange(opt) }), opt] }, opt))) }));
        case 'multi_choice': {
            const selected = Array.isArray(value) ? value : [];
            return (_jsx("div", { className: "space-y-2", children: (q.options ?? []).map((opt) => (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: selected.includes(opt), onChange: (e) => {
                                if (e.target.checked)
                                    onChange([...selected, opt]);
                                else
                                    onChange(selected.filter((x) => x !== opt));
                            } }), opt] }, opt))) }));
        }
        case 'scale_1_5':
        case 'scale_1_10':
        case 'nps': {
            const max = q.type === 'scale_1_5' ? 5 : 10;
            const min = q.type === 'nps' ? 0 : 1;
            const cells = [];
            for (let n = min; n <= max; n++)
                cells.push(n);
            return (_jsxs("div", { className: "flex flex-wrap gap-1.5", children: [cells.map((n) => {
                        const active = value === n;
                        return (_jsx("button", { type: "button", onClick: () => {
                                onChange(n);
                                if (q.type === 'nps')
                                    onNps(n);
                            }, className: `min-w-[2.25rem] rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${active
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-input bg-background hover:bg-muted'}`, children: n }, n));
                    }), q.type === 'nps' && (_jsxs("div", { className: "mt-2 flex w-full justify-between text-[10px] text-muted-foreground", children: [_jsx("span", { children: "0 \u2014 Not at all likely" }), _jsx("span", { children: "10 \u2014 Extremely likely" })] }))] }));
        }
        default:
            return null;
    }
}
